import { Controller } from '@hotwired/stimulus'

/**
 * Shipping Loading Controller
 * 
 * Manages the loading state for the shipping options section.
 * Shows loading overlay by default (in HTML) and hides it when controller connects.
 * This ensures that stale cached content is never visible - user sees loading until fresh content.
 * 
 * Also handles:
 * - Browser back/forward cache (bfcache) restoration via pageshow event
 * - Turbo navigation showing loading during page transitions
 * - Turbo form submissions that update shipping rates
 * 
 * Targets:
 *   - loadingOverlay: The overlay element to show/hide during loading
 *   - ratesContainer: The shipping rates list container
 */
export default class extends Controller {
  static targets = ['loadingOverlay', 'ratesContainer']

  connect() {
    // Hide loading immediately on fresh page load - content is already fresh from server
    this.hideLoading()
    
    // Turbo form submission events
    this.boundShowLoading = this.showLoading.bind(this)
    this.boundHideLoading = this.hideLoading.bind(this)
    
    document.addEventListener('turbo:submit-start', this.boundShowLoading)
    document.addEventListener('turbo:before-stream-render', this.boundHideLoading)
    
    // Show loading when navigating away (so if user comes back, they see loading)
    this.boundShowOnBeforeVisit = this.showOnBeforeVisit.bind(this)
    document.addEventListener('turbo:before-visit', this.boundShowOnBeforeVisit)
    
    // Handle browser back/forward cache - reload page if restored from bfcache
    this.boundHandlePageShow = this.handlePageShow.bind(this)
    window.addEventListener('pageshow', this.boundHandlePageShow)
  }

  disconnect() {
    document.removeEventListener('turbo:submit-start', this.boundShowLoading)
    document.removeEventListener('turbo:before-stream-render', this.boundHideLoading)
    document.removeEventListener('turbo:before-visit', this.boundShowOnBeforeVisit)
    window.removeEventListener('pageshow', this.boundHandlePageShow)
  }

  /**
   * Handle pageshow event - detects bfcache restoration
   * When page is restored from bfcache, reload to get fresh shipping rates
   */
  handlePageShow(event) {
    if (event.persisted) {
      // Page was restored from bfcache - reload to get fresh data
      window.location.reload()
    }
  }

  /**
   * Show loading when navigating away from checkout
   * This ensures if user navigates back, they see loading (not stale rates)
   */
  showOnBeforeVisit() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('d-none')
    }
    if (this.hasRatesContainerTarget) {
      this.ratesContainerTarget.classList.add('opacity-50')
    }
  }

  showLoading(event) {
    const form = event.target
    if (!form || !this.isAddressRelatedForm(form)) return

    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('d-none')
    }
    
    if (this.hasRatesContainerTarget) {
      this.ratesContainerTarget.classList.add('opacity-50')
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add('d-none')
    }
    
    if (this.hasRatesContainerTarget) {
      this.ratesContainerTarget.classList.remove('opacity-50')
    }
  }

  isAddressRelatedForm(form) {
    const action = form.getAttribute('action') || ''
    return action.includes('submit_address') || 
           action.includes('select_stored') || 
           action.includes('select_temp_dropship') ||
           action.includes('reset_to_original')
  }
}
